import React, { useState } from "react";
import { Constants } from "@eagerdog/constants";
import { IFeedPostItem } from "@eagerdog/interfaces";

import { apiService } from "src/services/api.service";
import { toast } from "src/components/Toast/ToastManager";
import Button from "src/components/Button/Button";
import TextArea from "src/components/TextArea/TextArea";

import styles from "./EditPostModal.module.scss";

interface IProps {
    post: IFeedPostItem;
    onClose: () => void;
    /** Called after a successful save so the card can reload. */
    onSaved: () => void;
}

/** Same ceiling as the composers and UpdatePostDto. */
const BODY_MAX = 2000;

/**
 * Text-only edit of a post the viewer may change (EDIT_POST_PRD). Club
 * posts go through the club endpoint (manager permission), everything else
 * through the owner endpoint, mirroring Delete in PostCardMenu.
 */
const EditPostModal: React.FC<IProps> = ({ post, onClose, onSaved }) => {
    const original = (post.body || "").trim();
    const [body, setBody] = useState(post.body || "");
    const [submitting, setSubmitting] = useState(false);

    const changed = body.trim() !== original;

    const save = async () => {
        if (!changed || submitting) return;
        setSubmitting(true);
        try {
            if (post.author_type === Constants.post_author_type.club) {
                await apiService.updateClubPost(post.author_id, post._id, { body: body.trim() });
            } else {
                await apiService.updatePost(post._id, { body: body.trim() });
            }
            onSaved();
            onClose();
        } catch (err: any) {
            const message = err?.response?.data?.message;
            toast.show({
                title: "Couldn't save changes",
                content: typeof message === "string" ? message : "Please try again.",
                duration: 6000,
                type: "fail",
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className={styles.EditPostModal}>
            <div className={styles.title}>Edit post</div>
            <p className={styles.subtitle}>Change the text. Photos, videos and tagged dogs stay as they are.</p>

            <div className={styles.field}>
                <TextArea
                    className={styles.input}
                    label="Text"
                    placeholder="Say something about this post…"
                    value={body}
                    onChange={(e) => setBody(e.target.value.slice(0, BODY_MAX))}
                />
                <div className={styles.counter}>{body.length} / {BODY_MAX}</div>
            </div>

            <div className={styles.footer}>
                <Button variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
                <Button onClick={save} disabled={!changed} isLoading={submitting}>Save</Button>
            </div>
        </div>
    );
};

export default EditPostModal;
